import { useReactToPrint } from 'react-to-print';
import { styled, Box, Button, Dialog, TextField, DialogActions, DialogContent, DialogTitle, Snackbar, Alert } from '@mui/material';
import useTableRequestParams from 'hooks/useTableRequestParams';
import Table from '@Components/table';
import { AppBarWithApiRef } from '@Components/app-bar';
import {
  useLazyVehicleReportQuery,
  useVehicleReportBySelectedIdMutation,
  useVehicleReportQuery,
  useUpdateVehicleRegistrationMutation, // Import the mutation hook
} from '@Store/api/vehicleReportSlice';
import { vehicleReportColumns } from './columns';
import Papa from 'papaparse';
import { exportData } from '@Utils/export-csv';
import { useEffect, useState } from 'react';
import { handleFetchError } from '@Utils/handleError';

const Container = styled(Box)`
  padding: 0px;
  display: flex;
  flex-direction: column;
  width: 100%;
  height: 100%;
`;

export function VehicleReport() {
  const tableParams = useTableRequestParams();
  const { data: vehicleReport, error: tableDataError } = useVehicleReportQuery(tableParams);
  const [getAllVehicleReport] = useLazyVehicleReportQuery();
  const [fetchSelectedVehiclesReport] = useVehicleReportBySelectedIdMutation();
  
  // Use the mutation from the slice
  const [updateVehicleRegistration, { isLoading: isUpdating }] = useUpdateVehicleRegistrationMutation();

  // State for update registration dialog
  const [updateDialogOpen, setUpdateDialogOpen] = useState(false);
  const [selectedVehicle, setSelectedVehicle] = useState<any>(null);
  const [newRegistrationNumber, setNewRegistrationNumber] = useState('');
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' as 'success' | 'error' });

  const handlePrint = useReactToPrint({
    content: () => document.getElementById('vehicle-report-container'),
  });

  const handleExport = async (selectedIds?: string[]) => {
    const dataToExport = [] as any[];
    
    if (selectedIds) {
      const history = await fetchSelectedVehiclesReport(selectedIds).unwrap();
      history?.forEach((data: any) => {
        dataToExport.push([
          ...vehicleReportColumns.map((column) =>
            column.valueGetter!({ row: data } as any)
          ),
        ]);
      });
    } else {
      const history = await getAllVehicleReport({
        getAllPages: true,
      } as any).unwrap();
      history?.items?.forEach((data) => {
        dataToExport.push([
          ...vehicleReportColumns.map((column) =>
            column.valueGetter!({ row: data } as any)
          ),
        ]);
      });
    }
    const csv = Papa.unparse({
      fields: vehicleReportColumns.map((column) => column.headerName!),
      data: dataToExport,
    });
    exportData(csv);
  };

  // Handle update registration number using the slice mutation
  const handleUpdateRegistration = (vehicle: any) => {
    setSelectedVehicle(vehicle);
    setNewRegistrationNumber(vehicle.registrationNumber || '');
    setUpdateDialogOpen(true);
  };

  const handleConfirmUpdate = async () => {
    if (!selectedVehicle || !newRegistrationNumber.trim()) {
      setSnackbar({
        open: true,
        message: 'Please enter a valid registration number',
        severity: 'error'
      });
      
      return;
    }

    try {
      // Use the mutation from the slice
      await updateVehicleRegistration({
        id: selectedVehicle.id,
        reg: newRegistrationNumber.trim()
      }).unwrap();

      setSnackbar({
        open: true,
        message: 'Registration number updated successfully',
        severity: 'success'
      });
      
      setUpdateDialogOpen(false);
      // The data will be automatically refetched due to invalidatesTags in the mutation
    } catch (error) {
      setSnackbar({
        open: true,
        message: 'Failed to update registration number',
        severity: 'error'
      });
    }
  };

  // Enhanced columns with update action
  const enhancedColumns = [
    ...vehicleReportColumns,
    {
      field: 'actions',
      headerName: 'Actions',
      width: 150,
      renderCell: (params: any) => (
        <Button
          variant="outlined"
          size="small"
          onClick={() => handleUpdateRegistration(params.row)}
        >
          Update Reg
        </Button>
      ),
      sortable: false,
      filterable: false,
    },
  ];

  useEffect(() => {
    handleFetchError(tableDataError);
  }, [tableDataError]);

  return (
    <Container id='vehicle-report-container'>
      <Table
        checkboxSelection
        printable
        searchable
        columns={enhancedColumns}
        handleExport={handleExport}
        handlePrint={handlePrint}
        paginated={
          vehicleReport?.items.length ? vehicleReport.items.length > 0 : false
        }
        rows={vehicleReport?.items || []}
        toolbar={AppBarWithApiRef}
        toolbarTitle='Vehicle Report'
        totalCount={vehicleReport?.count}
      />

      {/* Update Registration Dialog */}
      <Dialog open={updateDialogOpen} onClose={() => !isUpdating && setUpdateDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Update Registration Number</DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            margin="dense"
            label="New Registration Number"
            type="text"
            fullWidth
            variant="outlined"
            value={newRegistrationNumber}
            onChange={(e) => setNewRegistrationNumber(e.target.value)}
            onKeyPress={(e) => {
              if (e.key === 'Enter' && !isUpdating) {
                handleConfirmUpdate();
              }
            }}
            disabled={isUpdating}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setUpdateDialogOpen(false)} disabled={isUpdating}>
            Cancel
          </Button>
          <Button 
            onClick={handleConfirmUpdate} 
            variant="contained" 
            disabled={isUpdating || !newRegistrationNumber.trim()}
          >
            {isUpdating ? 'Updating...' : 'Update'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar for notifications */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert 
          onClose={() => setSnackbar({ ...snackbar, open: false })} 
          severity={snackbar.severity}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Container>
  );
}